import {
    Button,
    Caption,
    ProductInfo as AriaProductInfo,
    useAppDisplayModeContext,
    useStyles,
    pxToRem,
} from '@archipro-design/aria';
import { Link, useFetcher } from 'react-router';
import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import type { ModuleProduct } from '@modules/page-builder/api/get-module-product.server';
import type { PageBuilderGetModuleProductAction } from '~/routes/remix-api.page-builder.get-module-product';
import { QUERY_MODULE_PRODUCT } from '~/routes/remix-api.page-builder.get-module-product';
import { AriaProductInfoStyle } from '@modules/page-builder/component/common/product-info/ProductInfoArticle.style';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import { SingleImageWithProductContainer } from '@modules/page-builder/component/detail-article/detail-single-image-shell/DetailSingleImageShell.style';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { buildVariantDots } from '@modules/tile/util/build-variant-dots';

interface ProductInfoArticleProps {
    productId: number;
    variant?: '01' | '02';
    image?: ReactNode;
    clickable: boolean;
    dimension?: ColumnDimensionType;
}

const ProductInfoArticle = ({
    productId,
    variant,
    image,
    clickable,
    dimension,
}: ProductInfoArticleProps) => {
    const { mobile } = useAppDisplayModeContext();
    const fetcher = useFetcher<PageBuilderGetModuleProductAction>();
    const { css } = useStyles({ dimension, variant });

    const [productInfo, setProductInfo] = useState<ModuleProduct>();

    const fetchProductInfo = () => {
        if (!productInfo && fetcher.state === 'idle') {
            fetcher.load(
                `${QUERY_MODULE_PRODUCT}?productId=${encodeURIComponent(productId)}`,
            );
        }
    };

    useEffect(() => {
        fetchProductInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fetcherIsDone(fetcher) && fetcher.data) {
            setProductInfo(fetcher.data);
        }
    }, [fetcher]);

    const { productLink, variants, additionalVariantCount, ...rest } =
        productInfo || {};

    const { variantDots, adjustedAdditionalCount } = buildVariantDots({
        variants,
        additionalVariantCount,
        mobile,
    });

    const cta =
        clickable && productLink ? (
            <Button
                as={Link}
                to={productLink}
                size={14}
                variables={{
                    buttonPadding: pxToRem(9),
                    buttonSize14TextFontSize: pxToRem(12),
                }}
            >
                VIEW PRODUCT
            </Button>
        ) : (
            <Button
                size={14}
                variables={{
                    buttonPadding: pxToRem(9),
                    buttonSize14TextFontSize: pxToRem(12),
                }}
            >
                VIEW PRODUCT
            </Button>
        );

    const productDetails = productInfo ? (
        <AriaProductInfo
            {...rest}
            className={css(AriaProductInfoStyle)}
            productName={
                <Caption variant="04" maximumLines={1}>
                    {productInfo.productName}
                </Caption>
            }
            professionalName={{
                children: (
                    <Caption variant="04" maximumLines={1}>
                        {productInfo.professionalName}
                    </Caption>
                ),
            }}
            variants={variantDots}
            additionalVariantCount={adjustedAdditionalCount}
            variant={variant || '01'}
            {...(variant === '02' ? { cta } : {})}
        />
    ) : null;

    const visualContent = (
        <>
            {image}
            {productDetails}
        </>
    );

    return (
        <div className={css(SingleImageWithProductContainer)}>
            {clickable && productLink ? (
                <Link to={productLink}>{visualContent}</Link>
            ) : (
                visualContent
            )}
        </div>
    );
};

export default ProductInfoArticle;
